/**
 * `/admin` — Centre de pilotage Fonbex.
 *
 * Agrège les KPIs vitaux :
 *   • Utilisateurs, bêta-testeurs, nouveaux inscrits (7j/30j)
 *   • Organisations (clients réels vs démo)
 *   • Abonnements actifs / essais / MRR / chiffre d'affaires cumulé
 *   • Offres commerciales live (Bêta / Lancement / Standard)
 *   • Support (tickets ouverts / critiques)
 *   • Santé des services (MongoDB · Stripe · Resend · Emergent LLM)
 *   • Alertes système (webhooks Stripe échoués…)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminPageHeader, AdminCard, AdminKpi } from "./ui";
import { Button } from "@/components/ui/button";
import {
  Users, Building2, Sparkles, TrendingUp, ArrowRight, Rocket, Wallet,
  MessageCircle, Activity, AlertTriangle, RefreshCw, CheckCircle2, XCircle,
  Server, Mail, CreditCard, Brain,
} from "lucide-react";
import SmartLoader from "@/components/common/SmartLoader";
import { toast } from "sonner";

const QUICK_ACTIONS = [
  { to: "/admin/accounts", label: "Voir tous les comptes", icon: Users, tid: "admin-quick-accounts" },
  { to: "/admin/commercial", label: "Piloter l'offre commerciale", icon: TrendingUp, tid: "admin-quick-commercial" },
  { to: "/admin/support", label: "Consulter les retours", icon: MessageCircle, tid: "admin-quick-support" },
  { to: "/admin/technical", label: "Journal d'audit & diagnostics", icon: Activity, tid: "admin-quick-technical" },
];

const SERVICE_META = {
  mongodb: { label: "MongoDB", icon: Server },
  stripe: { label: "Stripe", icon: CreditCard },
  resend: { label: "Resend (E-mails)", icon: Mail },
  emergent_llm: { label: "Emergent LLM / OCR", icon: Brain },
};

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [offer, setOffer] = useState(null);
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setErr(null);
    try {
      const [ov, of_, hlth] = await Promise.all([
        api.get("/platform-admin/overview"),
        api.get("/landing/offer-status"),
        api.get("/platform-admin/services-health").catch(() => ({ data: null })),
      ]);
      setOverview(ov.data); setOffer(of_.data); setHealth(hlth.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Impossible de charger le tableau de bord");
    }
  };
  useEffect(() => { load(); }, []);

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/platform-admin/mrr/sync");
      toast.success(`MRR resynchronisé — ${eur(data.subscriptions.mrr_eur)}`);
      setOverview((old) => old ? { ...old, subscriptions: data.subscriptions } : old);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync impossible");
    } finally { setSyncing(false); }
  };

  if (!overview || !offer || err) {
    return (
      <div className="text-white">
        <SmartLoader label="Chargement du back-office…" onRetry={load} error={err} />
      </div>
    );
  }

  const beta = offer.beta;
  const launch = offer.launch;
  const subs = overview.subscriptions || {};
  const support = overview.support || {};
  const alerts = overview.alerts || {};
  const failedPayments = subs.failed_payments_30d || 0;
  const totalAlerts = (alerts.failed_webhooks_7d || 0) + (support.critical_open || 0) + failedPayments;

  return (
    <div className="animate-fade-up">
      <AdminPageHeader
        overline={`Bonjour ${user?.first_name || user?.email?.split("@")[0] || ""}`}
        title="Centre de pilotage Fonbex"
        description="Vue en temps réel de la plateforme. MRR calculé en local, sync Stripe à la demande."
        testId="admin-dashboard-header"
        actions={
          <Button onClick={load} variant="outline"
                  className="border-white/20 text-white hover:bg-white/5"
                  data-testid="admin-dashboard-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rafraîchir
          </Button>
        }
      />

      {/* Bandeau alertes */}
      {totalAlerts > 0 && (
        <AdminCard className="p-4 mb-6 border-amber-500/40 bg-amber-500/5"
                   data-testid="admin-alerts-banner">
          <div className="flex items-center gap-3 text-amber-300">
            <AlertTriangle className="h-5 w-5" />
            <div className="text-sm">
              <strong>{totalAlerts} alerte(s) système</strong> —{" "}
              {support.critical_open > 0 && `${support.critical_open} ticket(s) critique(s). `}
              {failedPayments > 0 && `${failedPayments} paiement(s) échoué(s) 30j. `}
              {alerts.failed_webhooks_7d > 0 && `${alerts.failed_webhooks_7d} webhook(s) Stripe en échec sur 7j.`}
            </div>
          </div>
        </AdminCard>
      )}

      {/* ─── Commercial (revenus + abonnements) ─── */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-white/50">Commercial & revenus</div>
          <Button size="sm" variant="ghost" onClick={syncStripe} disabled={syncing}
                  className="text-xs text-accent hover:bg-accent/10"
                  data-testid="admin-dashboard-sync-stripe">
            <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Synchronisation…" : "Synchroniser avec Stripe"}
          </Button>
        </div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <AdminKpi label="MRR" value={eur(subs.mrr_eur)} tone="accent" testId="admin-kpi-mrr"
                    hint={`Cache : ${subs.cached_at ? new Date(subs.cached_at).toLocaleTimeString("fr-FR") : "—"}`} />
          <AdminKpi label="CA cumulé" value={eur(subs.revenue_eur)} tone="success"
                    testId="admin-kpi-revenue"
                    hint="Paiements Stripe réussis" />
          <AdminKpi label="Abonnements actifs" value={subs.total_active || 0}
                    testId="admin-kpi-subs-active" />
          <AdminKpi label="Essais en cours" value={subs.trials || 0} tone="warning"
                    testId="admin-kpi-trials"
                    hint="Fenêtre 14 j après inscription" />
        </div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mt-3">
          <AdminKpi label="Taux de conversion" value={`${subs.conversion_rate_pct || 0} %`}
                    tone={(subs.conversion_rate_pct || 0) >= 5 ? "success" : "neutral"}
                    testId="admin-kpi-conversion"
                    hint="Payants ÷ inscrits (30 j)" />
          <AdminKpi label="Paiements échoués" value={subs.failed_payments_30d || 0}
                    tone={(subs.failed_payments_30d || 0) > 0 ? "danger" : "success"}
                    testId="admin-kpi-failed-payments"
                    hint="Factures Stripe non payées (30 j)" />
          <AdminKpi label="Nouveaux clients (30j)" value={overview.organizations.new_7d || 0}
                    testId="admin-kpi-new-clients" />
          <AdminKpi label="Places lancement" value={`${launch.remaining_slots}/${launch.slots_limit}`}
                    tone="accent" testId="admin-kpi-launch-slots"
                    hint={launch.is_locked ? "Fermé — tarif standard actif" : "Ouvertes"} />
        </div>
      </section>

      {/* ─── Utilisateurs ─── */}
      <section className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-3">Utilisateurs</div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <AdminKpi label="Total utilisateurs" value={overview.users.total} testId="admin-kpi-users" />
          <AdminKpi label="Bêta-testeurs" value={overview.users.beta_testers || 0} tone="accent"
                    testId="admin-kpi-beta-testers" />
          <AdminKpi label="Nouveaux (7j)" value={overview.users.new_7d} testId="admin-kpi-users-new-7d" />
          <AdminKpi label="Nouveaux (30j)" value={overview.users.new_30d || 0}
                    testId="admin-kpi-users-new-30d" />
        </div>
      </section>

      {/* ─── Organisations ─── */}
      <section className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-3">Organisations</div>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <AdminKpi label="Organisations" value={overview.organizations.total} testId="admin-kpi-orgs" />
          <AdminKpi label="Clients réels" value={overview.organizations.real} tone="success" testId="admin-kpi-orgs-real" />
          <AdminKpi label="Comptes démo" value={overview.organizations.demo} testId="admin-kpi-orgs-demo" />
          <AdminKpi label="Nouvelles (7j)" value={overview.organizations.new_7d} testId="admin-kpi-orgs-new" />
        </div>
      </section>

      {/* ─── Offres commerciales ─── */}
      <section className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-3">Offres commerciales</div>
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
          <AdminCard className="p-5" data-testid="admin-offer-beta">
            <div className="flex items-center gap-2 text-accent">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-widest">Bêta -50 %</span>
            </div>
            <div className="mt-3 font-serif text-3xl">{beta.discounted_price_eur} €<span className="text-sm text-white/50">/mois</span></div>
            <div className="text-xs text-white/60 mt-1">
              {beta.remaining_slots}/{beta.slots_limit} places restantes
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${Math.min(100, (beta.slots_used / beta.slots_limit) * 100)}%` }} />
            </div>
          </AdminCard>
          <AdminCard className="p-5" data-testid="admin-offer-launch">
            <div className="flex items-center gap-2 text-primary">
              <Rocket className="h-4 w-4 text-accent" />
              <span className="text-xs uppercase tracking-widest">Lancement</span>
            </div>
            <div className="mt-3 font-serif text-3xl">{launch.current_price_eur} €<span className="text-sm text-white/50">/mois</span></div>
            <div className="text-xs text-white/60 mt-1">
              {launch.remaining_slots}/{launch.slots_limit} places restantes {launch.is_locked && "(fermé)"}
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${Math.min(100, (launch.slots_used / launch.slots_limit) * 100)}%` }} />
            </div>
          </AdminCard>
          <AdminCard className="p-5" data-testid="admin-offer-standard">
            <div className="flex items-center gap-2 text-white/70">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs uppercase tracking-widest">Standard</span>
            </div>
            <div className="mt-3 font-serif text-3xl">{launch.regular_price_eur} €<span className="text-sm text-white/50">/mois</span></div>
            <div className="text-xs text-white/50 mt-1">
              Actif une fois les 250 premiers clients atteints.
            </div>
          </AdminCard>
        </div>
      </section>

      {/* ─── Support & Santé services ─── */}
      <section className="mb-8 grid gap-4 lg:grid-cols-2">
        {/* Support */}
        <AdminCard className="p-5" data-testid="admin-support-summary">
          <div className="flex items-center gap-2 text-white/80 mb-3">
            <MessageCircle className="h-4 w-4 text-accent" />
            <div className="text-[10px] uppercase tracking-widest text-white/50">Support</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Tickets ouverts</div>
              <div className="mt-1 font-mono text-3xl">{support.open_tickets || 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Critiques non résolus</div>
              <div className={`mt-1 font-mono text-3xl ${support.critical_open > 0 ? "text-rose-400" : ""}`}>
                {support.critical_open || 0}
              </div>
            </div>
          </div>
          <Link to="/admin/support"
                className="mt-4 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                data-testid="admin-support-link">
            Voir tous les tickets <ArrowRight className="h-3 w-3" />
          </Link>
        </AdminCard>

        {/* Santé services */}
        <AdminCard className="p-5" data-testid="admin-services-health">
          <div className="flex items-center gap-2 text-white/80 mb-3">
            <Server className="h-4 w-4 text-accent" />
            <div className="text-[10px] uppercase tracking-widest text-white/50">Santé services</div>
          </div>
          <ul className="space-y-2 text-sm">
            {health?.services ? Object.entries(health.services).map(([key, svc]) => {
              const meta = SERVICE_META[key] || { label: key, icon: Server };
              const Icon = meta.icon;
              const ok = svc.status === "ok" || svc.status === "configured";
              return (
                <li key={key} className="flex items-center justify-between text-white/80"
                    data-testid={`admin-service-${key}`}>
                  <span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {meta.label}</span>
                  <span className="flex items-center gap-1.5 text-xs">
                    {ok
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      : <XCircle className="h-3.5 w-3.5 text-rose-400" />}
                    <span className="text-white/60">{svc.status}{svc.latency_ms != null && ` · ${svc.latency_ms} ms`}{svc.mode && ` · ${svc.mode}`}</span>
                  </span>
                </li>
              );
            }) : (
              <li className="text-white/50 text-xs italic">Santé services indisponible.</li>
            )}
          </ul>
        </AdminCard>
      </section>

      {/* ─── Raccourcis ─── */}
      <section>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-3">Raccourcis</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {QUICK_ACTIONS.map(({ to, label, icon: Icon, tid }) => (
            <Link
              key={to}
              to={to}
              data-testid={tid}
              className="rounded-2xl border border-white/10 bg-[#161A1E] p-4 hover:border-accent/40 hover:bg-[#1A1E22] transition-colors flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-accent/10 p-2 text-accent">
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-sm text-white/90">{label}</span>
              </div>
              <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-accent group-hover:translate-x-0.5 transition" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
